import type { Rarity } from '../rarity';

/**
 * The ten artifacts and what they do (prompt 3D).
 *
 * Artifacts are NOT equipped: there is no slot, and every definition an
 * account owns contributes at the same time. What a copy is worth is decided
 * here and nowhere else - the browser never sends an effect, a level or a
 * rarity, and the server never reads one from a cached column.
 *
 * ## Integers only
 *
 * Every value below is an exact integer in a fixed unit, because these numbers
 * reach damage, health, attack speed and Gold:
 *
 * - percentages are **basis points** (500 bps = 5%);
 * - flat Gold per kill is **whole Gold**;
 * - health regeneration is in **milli-health per second** (1000 = 1 hp/s);
 * - attack speed is in **micro-attacks per second** (60_000 = 0.06 ASPD);
 * - the future Baby Alien chance is in **micro-percentage-points**
 *   (1000 = 0.001 pp).
 *
 * A float here would drift the moment it was doubled ten times, and these are
 * doubled ten times by design.
 */

/** `value(level) = level1 * 2^(level - 1)`, exact in bigint. */
export const ARTIFACT_MAX_LEVEL = 10;

/** One artifact's level-1 numbers. Absent keys mean "this effect is zero". */
export interface ArtifactEffects {
  /** Damage bonus, basis points. */
  readonly damageBps?: number;
  /** Maximum-health bonus, basis points. */
  readonly maxHealthBps?: number;
  /** Flat base health added before percentages. */
  readonly baseHealth?: number;
  /** Base attack speed, MICRO-attacks per second: 60_000 = 0.06/s. */
  readonly baseAttackSpeedMicro?: number;
  /** Flat Gold per killed enemy, whole Gold. */
  readonly goldPerKill?: number;
  /** Gold bonus, basis points. */
  readonly goldBps?: number;
  /** Health regenerated per second, MILLI-health: 1000 = 1 hp/s. */
  readonly regenMilliPerSecond?: number;
  /**
   * Extra chance of a Baby Alien, in MICRO percentage points: 1000 = 0.001 pp.
   *
   * Modelled and displayed only. Baby Aliens do not exist yet - no definition,
   * no sprite, no drop - and this number cannot produce one. See §10 of the
   * prompt and `docs/OPEN_QUESTIONS.md`.
   */
  readonly babyAlienChanceMicroPp?: number;
}

export interface ArtifactDefinition {
  /** Permanent definition id, matching the vault catalog. */
  readonly id: string;
  /** Display name. Real content, decided with the art. */
  readonly name: string;
  readonly rarity: Rarity;
  /** Asset id in the generated manifest. */
  readonly assetKey: string;
  /** Level-1 values; every one doubles per level. */
  readonly level1: ArtifactEffects;
}

/**
 * The ten artifacts, one per rarity.
 *
 * The rarity does NOT change how likely a drop is - every artifact is equally
 * likely (see `ARTIFACT_DROP`). It changes how strong the artifact is.
 */
export const ARTIFACT_DEFINITIONS: readonly ArtifactDefinition[] = Object.freeze([
  {
    id: 'artifact_guns',
    name: 'Guns',
    rarity: 'common',
    assetKey: 'artifact_guns',
    level1: { damageBps: 500 },
  },
  {
    id: 'artifact_great',
    name: 'Great',
    rarity: 'uncommon',
    assetKey: 'artifact_great',
    level1: { maxHealthBps: 200 },
  },
  {
    id: 'artifact_moneymoney',
    name: 'Money Money',
    rarity: 'rare',
    assetKey: 'artifact_moneymoney',
    level1: { goldPerKill: 1 },
  },
  {
    id: 'artifact_thecure',
    name: 'The Cure',
    rarity: 'epic',
    assetKey: 'artifact_thecure',
    level1: { regenMilliPerSecond: 1000 },
  },
  {
    id: 'artifact_rich',
    name: 'Rich',
    rarity: 'mythic',
    assetKey: 'artifact_rich',
    level1: { goldBps: 300 },
  },
  {
    id: 'artifact_speedfast',
    name: 'Speed Fast',
    rarity: 'legendary',
    assetKey: 'artifact_speedfast',
    level1: { baseAttackSpeedMicro: 60_000 },
  },
  {
    id: 'artifact_mothership',
    name: 'Mother Ship',
    rarity: 'excellent',
    assetKey: 'artifact_mothership',
    level1: { maxHealthBps: 600, baseHealth: 2 },
  },
  {
    id: 'artifact_doublekill',
    name: 'Double Kill',
    rarity: 'perfect',
    assetKey: 'artifact_doublekill',
    level1: { damageBps: 800, baseAttackSpeedMicro: 30_000 },
  },
  {
    id: 'artifact_othergame',
    name: 'Other Game',
    rarity: 'anomaly',
    assetKey: 'artifact_othergame',
    level1: { goldBps: 1000, babyAlienChanceMicroPp: 1000 },
  },
  {
    id: 'artifact_yeeaaah',
    name: 'Yeeeaaah',
    rarity: 'prismatic',
    assetKey: 'artifact_yeeaaah',
    level1: {
      damageBps: 1200,
      baseAttackSpeedMicro: 60_000,
      baseHealth: 4,
      goldPerKill: 1,
    },
  },
]);

const ARTIFACTS_BY_ID: ReadonlyMap<string, ArtifactDefinition> = new Map(
  ARTIFACT_DEFINITIONS.map((artifact) => [artifact.id, artifact]),
);

export function findArtifact(id: string): ArtifactDefinition | null {
  return ARTIFACTS_BY_ID.get(id) ?? null;
}

export function isArtifactDefinitionId(id: string): boolean {
  return ARTIFACTS_BY_ID.has(id);
}

/* -------------------------------------------------------------------------- */
/*  Level scaling                                                             */
/* -------------------------------------------------------------------------- */

/**
 * `value(level) = level1 * 2^(level - 1)`, in bigint.
 *
 * At level 10 that is a 512x multiplier, which is why nothing here is a
 * float: 0.06 attacks/second becomes 30.72, and it is stored as
 * 30_720_000 micro-attacks - exact, and never rounded on the way.
 */
export function scaleToLevel(level1Value: bigint, level: number): bigint {
  if (!Number.isSafeInteger(level) || level < 1 || level > ARTIFACT_MAX_LEVEL) {
    throw new RangeError(`artifact level must be an integer in [1, ${ARTIFACT_MAX_LEVEL}]`);
  }
  return level1Value * 2n ** BigInt(level - 1);
}

/** Every effect of one artifact at one level, as exact bigints. */
export interface ScaledArtifactEffects {
  readonly damageBps: bigint;
  readonly maxHealthBps: bigint;
  readonly baseHealth: bigint;
  readonly baseAttackSpeedMicro: bigint;
  readonly goldPerKill: bigint;
  readonly goldBps: bigint;
  readonly regenMilliPerSecond: bigint;
  readonly babyAlienChanceMicroPp: bigint;
}

export const NO_ARTIFACT_EFFECTS: ScaledArtifactEffects = Object.freeze({
  damageBps: 0n,
  maxHealthBps: 0n,
  baseHealth: 0n,
  baseAttackSpeedMicro: 0n,
  goldPerKill: 0n,
  goldBps: 0n,
  regenMilliPerSecond: 0n,
  babyAlienChanceMicroPp: 0n,
});

export function effectsAtLevel(
  definition: ArtifactDefinition,
  level: number,
): ScaledArtifactEffects {
  const at = (value: number | undefined): bigint =>
    value === undefined ? 0n : scaleToLevel(BigInt(value), level);
  return {
    damageBps: at(definition.level1.damageBps),
    maxHealthBps: at(definition.level1.maxHealthBps),
    baseHealth: at(definition.level1.baseHealth),
    baseAttackSpeedMicro: at(definition.level1.baseAttackSpeedMicro),
    goldPerKill: at(definition.level1.goldPerKill),
    goldBps: at(definition.level1.goldBps),
    regenMilliPerSecond: at(definition.level1.regenMilliPerSecond),
    babyAlienChanceMicroPp: at(definition.level1.babyAlienChanceMicroPp),
  };
}

/** Adds two effect sets. Different artifacts stack; copies never do. */
export function addEffects(
  a: ScaledArtifactEffects,
  b: ScaledArtifactEffects,
): ScaledArtifactEffects {
  return {
    damageBps: a.damageBps + b.damageBps,
    maxHealthBps: a.maxHealthBps + b.maxHealthBps,
    baseHealth: a.baseHealth + b.baseHealth,
    baseAttackSpeedMicro: a.baseAttackSpeedMicro + b.baseAttackSpeedMicro,
    goldPerKill: a.goldPerKill + b.goldPerKill,
    goldBps: a.goldBps + b.goldBps,
    regenMilliPerSecond: a.regenMilliPerSecond + b.regenMilliPerSecond,
    babyAlienChanceMicroPp: a.babyAlienChanceMicroPp + b.babyAlienChanceMicroPp,
  };
}

/* -------------------------------------------------------------------------- */
/*  Drop                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Exact drop odds, as denominators - never a float probability.
 *
 * A boss is worth exactly six normal kills: 6000 / 6 = 1000, so the
 * relationship is a property of the numbers rather than a comment.
 */
export const ARTIFACT_DROP = Object.freeze({
  /** One in six thousand confirmed normal-mob kills. */
  normalKillDenominator: 6000,
  /** Exactly six times the normal chance. */
  bossMultiplier: 6,
  /** One in one thousand confirmed boss kills. */
  get bossKillDenominator(): number {
    return 6000 / 6;
  },
});

/**
 * Which artifact a successful roll produces: uniform over all ten.
 *
 * Rarity decides how STRONG an artifact is, never how likely it is. A
 * Prismatic Yeeeaaah is exactly as probable as a Common Guns.
 */
export const ARTIFACT_DROP_TABLE: readonly string[] = Object.freeze(
  ARTIFACT_DEFINITIONS.map((artifact) => artifact.id),
);
